import React, { useState, useEffect } from 'react';
import { CircleDot, Palette, Check, Sparkles, Loader2, PaintBucket, RefreshCw, ChevronDown } from 'lucide-react';

// Predefined themes for the scode
const SCODE_THEMES = {
  default: {
    name: 'Default',
    bgColor: '#2a2a2a',
    accentColor: '#10B981',
    textColor: '#ffffff',
    badgeColor: '#4a4a4a',
    style: 'standard'
  },
  corporate: {
    name: 'Corporate',
    bgColor: '#1E293B',
    accentColor: '#0EA5E9',
    textColor: '#ffffff',
    badgeColor: '#334155',
    style: 'standard'
  },
  emerald: {
    name: 'Emerald',
    bgColor: '#065F46',
    accentColor: '#4ADE80',
    textColor: '#FFFFFF',
    badgeColor: '#047857',
    style: 'standard'
  },
  google: {
    name: 'Google',
    bgColor: '#ffffff',
    accentColor: '#4285F4', // Google Blue
    textColor: '#202124', // Google Dark Gray for text
    badgeColor: '#F1F3F4', // Google Light Gray for backgrounds
    style: 'google',
    borderColor: '#DADCE0' // Google border color
  },
  light: {
    name: 'Light',
    bgColor: '#FFFFFF',
    accentColor: '#0891B2',
    textColor: '#334155',
    badgeColor: '#F1F5F9',
    style: 'standard',
    borderColor: '#E2E8F0'
  },
  custom: {
    name: 'Custom',
    bgColor: '#2a2a2a',
    accentColor: '#10B981',
    textColor: '#ffffff',
    badgeColor: '#4a4a4a',
    style: 'standard'
  }
};

// Constant green color for SECURED badge
const SECURED_BADGE_COLOR = '#10B981';

const ScodeCustomizationComponent = ({ 
  scodeSettings, 
  onChange, 
  onSave, 
  isSaving = false,
  saveSuccess = false
}) => {
  // Check if the user has previously saved custom colors
  const isExistingCustom = scodeSettings?.isCustom === true;
  
  const [activeTheme, setActiveTheme] = useState(
    isExistingCustom ? 'custom' : (scodeSettings?.theme || 'default')
  );
  
  const [customColors, setCustomColors] = useState({
    bgColor: scodeSettings?.bgColor || SCODE_THEMES.default.bgColor,
    accentColor: scodeSettings?.accentColor || SCODE_THEMES.default.accentColor,
    textColor: scodeSettings?.textColor || SCODE_THEMES.default.textColor,
    badgeColor: scodeSettings?.badgeColor || SCODE_THEMES.default.badgeColor,
    borderColor: scodeSettings?.borderColor || '',
    style: scodeSettings?.style || 'standard'
  });

  const [showCustomEditor, setShowCustomEditor] = useState(isExistingCustom);
  
  // Update custom colors when a theme is selected as a starting point
  useEffect(() => {
    if (activeTheme !== 'custom') {
      setCustomColors({
        bgColor: SCODE_THEMES[activeTheme].bgColor,
        accentColor: SCODE_THEMES[activeTheme].accentColor,
        textColor: SCODE_THEMES[activeTheme].textColor,
        badgeColor: SCODE_THEMES[activeTheme].badgeColor,
        borderColor: SCODE_THEMES[activeTheme].borderColor || '',
        style: SCODE_THEMES[activeTheme].style || 'standard'
      });
    }
  }, [activeTheme]);
  
  // Handle theme selection
  const handleThemeChange = (themeKey) => {
    setActiveTheme(themeKey);
    
    if (themeKey === 'custom') {
      setShowCustomEditor(true);
      
      if (onChange) {
        onChange({
          ...scodeSettings,
          theme: themeKey,
          ...customColors,
          isCustom: true
        });
      }
    } else {
      setShowCustomEditor(false);
      
      if (onChange) {
        onChange({
          ...scodeSettings,
          theme: themeKey,
          ...SCODE_THEMES[themeKey],
          isCustom: false
        });
      }
    }
  };
  
  // Handle custom color changes
  const handleColorChange = (colorType, value) => {
    const updatedColors = {
      ...customColors,
      [colorType]: value
    };
    
    setCustomColors(updatedColors);
    
    if (onChange && activeTheme === 'custom') {
      onChange({
        ...scodeSettings,
        theme: 'custom',
        ...updatedColors,
        isCustom: true
      });
    }
  };
  
  // Handle style toggle
  const handleStyleChange = (style) => {
    const updatedColors = {
      ...customColors,
      style
    };
    
    setCustomColors(updatedColors);
    
    if (onChange && activeTheme === 'custom') {
      onChange({
        ...scodeSettings,
        theme: 'custom',
        ...updatedColors,
        isCustom: true
      });
    }
  };
  
  // Preview of the scode with the selected theme
  const selectedTheme = activeTheme === 'custom' 
    ? customColors 
    : SCODE_THEMES[activeTheme];
  
  return (
    <div className="space-y-8">
      <div className="mb-6">
        <h3 className="text-xl font-medium text-midnight font-heading flex items-center gap-2 mb-2">
          <Sparkles className="h-5 w-5 text-violet" />
          Security Code Appearance
        </h3>
        <p className="text-sm text-ebony">
          Customize how your security code (scode) appears in emails you send. This code helps recipients verify the authenticity of your emails.
        </p>
      </div>
      
      {/* Preview Section - Moved to top for better UX */}
      <div className="relative rounded-xl border border-ebony border-opacity-20 bg-white shadow-md overflow-hidden">
        <div className="bg-gradient-to-r from-old-lace to-light-purple/20 px-6 py-4 border-b border-ebony border-opacity-20">
          <h4 className="text-sm font-medium text-gunmetal flex items-center font-heading">
            <div className="p-1 rounded-full bg-white mr-2 shadow-sm">
              <Palette className="h-4 w-4 text-violet" />
            </div>
            Preview
          </h4>
        </div>
        <div className="p-6 bg-white">
          <div className="mb-4 pb-4 border-b border-ebony border-opacity-10">
            <div style={{
              margin: '14px 0',
            }}>
              <div style={{
                background: selectedTheme.bgColor,
                borderRadius: '32px',
                padding: '4px 8px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '12px',
                border: selectedTheme.borderColor ? `1px solid ${selectedTheme.borderColor}` : 'none',
                borderLeft: `2px solid ${selectedTheme.accentColor}`,
              }}>
                <div style={{
                  background: selectedTheme.badgeColor,
                  padding: '4px 8px',
                  borderRadius: '16px',
                  color: selectedTheme.textColor,
                  fontSize: '10px',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}>
                  <span style={{
                    width: '4px',
                    height: '4px',
                    background: selectedTheme.accentColor,
                    borderRadius: '50%',
                    display: 'inline-block',
                  }}></span>
                  <span style={{ color: selectedTheme.accentColor }}></span>
                  STK3X4MP1E
                </div>
                
                {selectedTheme.style !== 'minimal' && (
                  <>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '12px'
                    }}>
                      <span style={{ 
                        color: selectedTheme.bgColor, 
                        fontSize: '14px'
                      }}>|</span>
                      <span style={{ 
                        color: selectedTheme.bgColor, 
                        fontSize: '14px'
                      }}>|</span>
                      <span style={{ 
                        color: selectedTheme.bgColor, 
                        fontSize: '14px'
                      }}>|</span>
                    </div>
                  </>
                )}
                
                <div style={{
                  background: selectedTheme.badgeColor,
                  padding: '4px 8px',
                  borderRadius: '16px',
                }}>
                  <span style={{
                    color: selectedTheme.textColor,
                    textDecoration: 'none',
                    fontSize: '10px',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}>
                    Verify Email & Sender
                    <span style={{ color: selectedTheme.accentColor }}>→</span>
                  </span>
                </div>
              </div>
              
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                marginTop: '4px',
                marginLeft: '2px',
              }}>
                <span style={{
                  fontSize: '10px',
                  color: '#666666',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}>
                  Email secured by Stickr
                </span>
                
                <div style={{
                  background: `rgba(${hexToRgb(SECURED_BADGE_COLOR)},0.15)`,
                  padding: '2px 6px',
                  borderRadius: '12px',
                  color: SECURED_BADGE_COLOR,
                  fontSize: '9px',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
                  fontWeight: '500',
                  letterSpacing: '0.5px',
                }}>SECURED</div>
              </div>
            </div>
          </div>
          
          {/* Explanation text */}
          <div className="text-xs text-ebony italic">
            This is how your security code will appear in your outgoing emails. The "SECURED" badge will always appear green to ensure recipients recognize the security indicator.
          </div>
        </div>
      </div>
      
      {/* Theme Selection */}
      <div className="rounded-xl border border-ebony border-opacity-20 bg-white shadow-md overflow-hidden">
        <div className="bg-gradient-to-r from-old-lace to-light-purple/20 px-6 py-4 border-b border-ebony border-opacity-20">
          <h4 className="text-sm font-medium text-gunmetal flex items-center font-heading">
            <div className="p-1 rounded-full bg-white mr-2 shadow-sm">
              <CircleDot className="h-4 w-4 text-violet" />
            </div>
            Choose a Theme
          </h4>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {Object.entries(SCODE_THEMES).map(([key, theme]) => (
              <button
                key={key}
                type="button"
                className={`relative rounded-lg border p-3 flex flex-col items-center justify-center gap-2 transition-all hover:shadow-md ${
                  activeTheme === key 
                    ? 'border-violet ring-2 ring-violet ring-opacity-50 shadow-sm' 
                    : 'border-ebony border-opacity-20 hover:border-ebony hover:border-opacity-40'
                }`}
                onClick={() => handleThemeChange(key)}
              >
                {activeTheme === key && (
                  <span className="absolute top-2 right-2">
                    <Check className="h-4 w-4 text-violet" />
                  </span>
                )}
                <div 
                  className="w-full h-8 rounded-md border overflow-hidden" 
                  style={{ 
                    backgroundColor: key === 'custom' ? customColors.bgColor : theme.bgColor,
                    borderColor: key === 'custom' ? (customColors.borderColor || customColors.bgColor) : (theme.borderColor || theme.bgColor)
                  }}
                >
                  <div 
                    className="w-1/3 h-full" 
                    style={{ backgroundColor: key === 'custom' ? customColors.accentColor : theme.accentColor }}
                  ></div>
                </div>
                <span className="text-xs font-medium text-gunmetal">
                  {theme.name}
                </span>
                {key === 'custom' && (
                  <span className="text-xs text-ebony">
                    <PaintBucket className="h-3 w-3 inline mb-0.5 mr-1" />
                    Your colors
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Custom Color Editor */}
      {showCustomEditor && (
        <div className="rounded-xl border border-ebony border-opacity-20 bg-white shadow-md overflow-hidden">
          <div className="bg-gradient-to-r from-old-lace to-light-purple/20 px-6 py-4 border-b border-ebony border-opacity-20 flex justify-between items-center">
            <h4 className="text-sm font-medium text-gunmetal flex items-center font-heading">
              <div className="p-1 rounded-full bg-white mr-2 shadow-sm">
                <PaintBucket className="h-4 w-4 text-violet" />
              </div>
              Custom Colors
            </h4>
            <button
              type="button"
              onClick={() => {
                const defaultTheme = 'default';
                setCustomColors({
                  ...SCODE_THEMES[defaultTheme]
                });
                handleThemeChange(defaultTheme);
              }}
              className="text-xs flex items-center text-ebony hover:text-gunmetal bg-white py-1 px-2 rounded border border-ebony border-opacity-20 shadow-sm hover:shadow"
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              Reset to Default
            </button>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Background Color */}
              <div className="bg-old-lace/30 p-4 rounded-lg">
                <label className="block text-sm font-medium text-gunmetal mb-2">
                  Background Color
                </label>
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-md border shadow-sm"
                    style={{ backgroundColor: customColors.bgColor }}
                  ></div>
                  <div className="flex-1">
                    <input
                      type="text"
                      value={customColors.bgColor}
                      onChange={(e) => handleColorChange('bgColor', e.target.value)}
                      className="text-sm border rounded-pill px-3 py-2 w-full shadow-sm focus:ring-1 focus:ring-violet focus:border-violet outline-none"
                      placeholder="#000000"
                    />
                  </div>
                  <input
                    type="color"
                    value={customColors.bgColor}
                    onChange={(e) => handleColorChange('bgColor', e.target.value)}
                    className="h-10 w-10 cursor-pointer rounded-md"
                  />
                </div>
              </div>

              {/* Accent Color */}
              <div className="bg-old-lace/30 p-4 rounded-lg">
                <label className="block text-sm font-medium text-gunmetal mb-2">
                  Accent Color
                </label>
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-md border shadow-sm"
                    style={{ backgroundColor: customColors.accentColor }}
                  ></div>
                  <div className="flex-1">
                    <input
                      type="text"
                      value={customColors.accentColor}
                      onChange={(e) => handleColorChange('accentColor', e.target.value)}
                      className="text-sm border rounded-pill px-3 py-2 w-full shadow-sm focus:ring-1 focus:ring-violet focus:border-violet outline-none"
                      placeholder="#000000"
                    />
                  </div>
                  <input
                    type="color"
                    value={customColors.accentColor}
                    onChange={(e) => handleColorChange('accentColor', e.target.value)}
                    className="h-10 w-10 cursor-pointer rounded-md"
                  />
                </div>
              </div>

              {/* Text Color */}
              <div className="bg-old-lace/30 p-4 rounded-lg">
                <label className="block text-sm font-medium text-gunmetal mb-2">
                  Text Color
                </label>
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-md border shadow-sm"
                    style={{ backgroundColor: customColors.textColor }}
                  ></div>
                  <div className="flex-1">
                    <input
                      type="text"
                      value={customColors.textColor}
                      onChange={(e) => handleColorChange('textColor', e.target.value)}
                      className="text-sm border rounded-pill px-3 py-2 w-full shadow-sm focus:ring-1 focus:ring-violet focus:border-violet outline-none"
                      placeholder="#000000"
                    />
                  </div>
                  <input
                    type="color"
                    value={customColors.textColor}
                    onChange={(e) => handleColorChange('textColor', e.target.value)}
                    className="h-10 w-10 cursor-pointer rounded-md"
                  />
                </div>
              </div>

              {/* Badge Color */}
              <div className="bg-old-lace/30 p-4 rounded-lg">
                <label className="block text-sm font-medium text-gunmetal mb-2">
                  Badge Color
                </label>
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-md border shadow-sm"
                    style={{ backgroundColor: customColors.badgeColor }}
                  ></div>
                  <div className="flex-1">
                    <input
                      type="text"
                      value={customColors.badgeColor}
                      onChange={(e) => handleColorChange('badgeColor', e.target.value)}
                      className="text-sm border rounded-pill px-3 py-2 w-full shadow-sm focus:ring-1 focus:ring-violet focus:border-violet outline-none"
                      placeholder="#000000"
                    />
                  </div>
                  <input
                    type="color"
                    value={customColors.badgeColor}
                    onChange={(e) => handleColorChange('badgeColor', e.target.value)}
                    className="h-10 w-10 cursor-pointer rounded-md"
                  />
                </div>
              </div>

              {/* Border Color (Optional) */}
              <div className="bg-old-lace/30 p-4 rounded-lg">
                <label className="block text-sm font-medium text-gunmetal mb-2">
                  Border Color (Optional)
                </label>
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-md border shadow-sm"
                    style={{ 
                      backgroundColor: customColors.borderColor || 'transparent',
                      backgroundImage: !customColors.borderColor ? 'linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc), linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc)' : 'none',
                      backgroundSize: '8px 8px',
                      backgroundPosition: '0 0, 4px 4px'
                    }}
                  ></div>
                  <div className="flex-1">
                    <input
                      type="text"
                      value={customColors.borderColor || ''}
                      onChange={(e) => handleColorChange('borderColor', e.target.value)}
                      className="text-sm border rounded-pill px-3 py-2 w-full shadow-sm focus:ring-1 focus:ring-violet focus:border-violet outline-none"
                      placeholder="Optional"
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    <input
                      type="color"
                      value={customColors.borderColor || '#ffffff'}
                      onChange={(e) => handleColorChange('borderColor', e.target.value)}
                      className="h-10 w-10 cursor-pointer rounded-md"
                    />
                    {customColors.borderColor && (
                      <button
                        type="button"
                        onClick={() => handleColorChange('borderColor', '')}
                        className="text-xs text-ebony hover:text-gunmetal p-1 hover:bg-old-lace/50 rounded"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Style Selection */}
              <div className="bg-old-lace/30 p-4 rounded-lg">
                <label className="block text-sm font-medium text-gunmetal mb-2">
                  Style Option
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleStyleChange('standard')}
                    className={`px-4 py-2 border rounded-pill ${
                      customColors.style === 'standard' 
                        ? 'bg-violet text-white border-violet' 
                        : 'bg-white text-gunmetal border-ebony border-opacity-50 hover:bg-old-lace/50'
                    }`}
                  >
                    Standard
                  </button>
                  <button
                    type="button"
                    onClick={() => handleStyleChange('minimal')}
                    className={`px-4 py-2 border rounded-pill ${
                      customColors.style === 'minimal' 
                        ? 'bg-violet text-white border-violet' 
                        : 'bg-white text-gunmetal border-ebony border-opacity-50 hover:bg-old-lace/50'
                    }`}
                  >
                    Minimal
                  </button>
                </div>
                <div className="mt-2 text-xs text-ebony">
                  {customColors.style === 'minimal' 
                    ? 'Minimal style removes separator bars between elements' 
                    : 'Standard style includes separator bars between elements'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Save Button */}
      <div className="flex justify-end mt-8">
        <button
          type="button"
          onClick={onSave}
          disabled={isSaving}
          className="inline-flex items-center px-5 py-2.5 border border-transparent rounded-pill shadow-md text-sm font-medium text-white bg-violet hover:bg-violet/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet disabled:opacity-50 transition-all"
        >
          {isSaving ? (
            <Loader2 className="animate-spin -ml-1 mr-2 h-5 w-5" />
          ) : saveSuccess ? (
            <Check className="-ml-1 mr-2 h-5 w-5" />
          ) : (
            <Palette className="-ml-1 mr-2 h-5 w-5" />
          )}
          {isSaving ? 'Saving...' : saveSuccess ? 'Saved!' : 'Save Appearance'}
        </button>
      </div>
    </div>
  );
};

// Helper function to convert hex to RGB
function hexToRgb(hex) {
  // Remove the hash if it exists
  hex = hex.replace('#', '');
  
  // Parse the hex values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  return `${r},${g},${b}`;
}

export default ScodeCustomizationComponent;